import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatCurrency, formatDate } from "../lib/api";
import { MODULE_SCHEMAS } from "../lib/moduleSchemas";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Plus, Pencil, Trash2, Search, FileText, Paperclip, ShieldAlert,
} from "lucide-react";
import EntityFormDialog from "../components/EntityFormDialog";
import MaskedField from "../components/MaskedField";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

function formatCell(schema, key, value) {
  if (value == null || value === "") return <span className="text-slate-400">—</span>;
  const field = schema.fields.find((f) => f.key === key);
  if (field?.sensitive) {
    return <MaskedField value={value} testid={`cell-${key}`} />;
  }
  if (field?.type === "number") {
    return <span className="tabular-nums">{formatCurrency(value)}</span>;
  }
  if (field?.type === "date") return formatDate(value);
  if (field?.type === "boolean") {
    return <Badge variant={value ? "default" : "outline"}>{value ? "Yes" : "No"}</Badge>;
  }
  return <span>{String(value)}</span>;
}

export default function EntityPage() {
  const { module } = useParams();
  const schema = MODULE_SCHEMAS[module];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialog, setDialog] = useState({ open: false, mode: "create", item: null });
  const [toDelete, setToDelete] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    if (!schema) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/entities/${module}`, { params: { mask: true, q: query || undefined } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line
  }, [module]);

  if (!schema) return <div className="p-6">Unknown module.</div>;

  const openCreate = () => setDialog({ open: true, mode: "create", item: null });
  const openEdit = async (item) => {
    // fetch unmasked
    const { data } = await api.get(`/entities/${module}/${item.id}?mask=false`);
    setDialog({ open: true, mode: "edit", item: data });
  };
  const handleSubmit = async (payload) => {
    try {
      if (dialog.mode === "edit") {
        await api.put(`/entities/${module}/${dialog.item.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/entities/${module}`, { module, ...payload });
        toast.success("Created");
      }
      await load();
    } catch (e) {
      toast.error("Save failed");
      throw e;
    }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/entities/${module}/${toDelete.id}`);
      toast.success("Deleted");
      setToDelete(null);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid={`module-page-${module}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-overline">Module</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            {schema.label}
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            {schema.sensitive?.length > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <ShieldAlert className="h-3.5 w-3.5" strokeWidth={1.75} />
                Sensitive fields in this module are encrypted and masked by default.
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onBlur={load}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search titles..."
              className="pl-9 w-56"
              data-testid={`module-search-${module}`}
            />
          </div>
          <Button onClick={openCreate} data-testid={`module-add-${module}-button`}>
            <Plus className="h-4 w-4 mr-2" /> Add {schema.label.split(" ")[0]}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500 text-sm">Loading...</div>
      ) : items.length === 0 ? (
        <Card className="p-10 border-dashed border-slate-300 bg-slate-50/50 text-center">
          <FileText className="h-8 w-8 mx-auto text-slate-400" strokeWidth={1.5} />
          <div className="mt-3 font-heading text-lg font-semibold">No entries yet</div>
          <div className="text-sm text-slate-500 mt-1">
            Add your first {schema.label.toLowerCase()} entry to get started.
          </div>
          <Button className="mt-6" onClick={openCreate} data-testid={`module-empty-add-${module}-button`}>
            <Plus className="h-4 w-4 mr-2" /> Add {schema.label}
          </Button>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden md:block overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70 text-left">
                    <th className="px-4 py-3 font-medium text-slate-700">Title</th>
                    {schema.list_columns.map((c) => {
                      const f = schema.fields.find((x) => x.key === c);
                      return (
                        <th key={c} className="px-4 py-3 font-medium text-slate-700">
                          {f?.label || c}
                        </th>
                      );
                    })}
                    <th className="px-4 py-3 font-medium text-slate-700">Updated</th>
                    <th className="px-4 py-3 font-medium text-slate-700 w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{item.title}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          {item.tags?.map((t) => (
                            <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                          ))}
                          {item.attachments?.length > 0 && (
                            <span className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" />{item.attachments.length}</span>
                          )}
                        </div>
                      </td>
                      {schema.list_columns.map((c) => (
                        <td key={c} className="px-4 py-3 text-slate-700">
                          {formatCell(schema, c, item.data?.[c])}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-slate-500 text-xs tabular-nums">
                        {formatDate(item.updated_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(item)}
                            data-testid={`row-edit-${item.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setToDelete(item)}
                            data-testid={`row-delete-${item.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {items.map((item) => (
              <Card key={item.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">{item.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{formatDate(item.updated_at)}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setToDelete(item)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  {schema.list_columns.map((c) => {
                    const f = schema.fields.find((x) => x.key === c);
                    return (
                      <div key={c} className="min-w-0">
                        <div className="text-slate-500 label-overline text-[9px]">{f?.label}</div>
                        <div className="mt-0.5 truncate">{formatCell(schema, c, item.data?.[c])}</div>
                      </div>
                    );
                  })}
                </div>
                <button
                  className="mt-3 text-xs font-medium text-slate-600 hover:text-slate-900"
                  onClick={() => setExpanded((m) => ({ ...m, [item.id]: !m[item.id] }))}
                >
                  {expanded[item.id] ? "Hide details" : "Show all fields"}
                </button>
                {expanded[item.id] && (
                  <div className="mt-3 space-y-2 text-xs">
                    {schema.fields.map((f) => (
                      <div key={f.key} className="flex justify-between gap-2">
                        <span className="text-slate-500">{f.label}</span>
                        <span className="text-right min-w-0">{formatCell(schema, f.key, item.data?.[f.key])}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      )}

      <EntityFormDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        schema={schema}
        initial={dialog.item}
        onSubmit={handleSubmit}
        title={dialog.mode === "edit" ? `Edit ${schema.label}` : `Add ${schema.label}`}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.title}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
